import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch, HiPencil, HiTrash } from 'react-icons/hi';
import { Button, Input, Select, Card, CardHeader, Table, Pagination, Modal } from '../components/ui';
import { listOwners, createOwner, updateOwner, deleteOwner, getLinkableOwnerUsers } from '../services/owners.api';
import { formatDate } from '../utils/helpers';
import { getErrorMessage } from '../services/api';
import type { Owner } from '../types';

export function Owners() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [userId, setUserId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['owners', { page, search }],
    queryFn: () => listOwners({ page, search }),
  });

  // Selectable users (role Owner, not yet linked) — an owner is created from one of these.
  const { data: linkableUsers } = useQuery({
    queryKey: ['linkableOwnerUsers'],
    queryFn: getLinkableOwnerUsers,
    enabled: isCreateOpen,
  });

  const createMutation = useMutation({
    mutationFn: () => createOwner({ userId, name, phone, address }),
    onSuccess: () => {
      toast.success('Owner erstellt');
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      queryClient.invalidateQueries({ queryKey: ['linkableOwnerUsers'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateOwner(selectedOwner!.id, { name, phone, address }),
    onSuccess: () => {
      toast.success('Owner aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      setIsEditOpen(false);
      setSelectedOwner(null);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteOwner(selectedOwner!.id),
    onSuccess: () => {
      toast.success('Owner deaktiviert');
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      setIsDeleteOpen(false);
      setSelectedOwner(null);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setUserId('');
  };

  const openEditModal = (owner: Owner, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOwner(owner);
    setName(owner.name);
    setEmail(owner.email);
    setPhone(owner.phone || '');
    setAddress(owner.address || '');
    setIsEditOpen(true);
  };

  const openDeleteModal = (owner: Owner, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOwner(owner);
    setIsDeleteOpen(true);
  };

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: 'Phone', render: (o: Owner) => o.phone || '-' },
    { key: 'partners', header: 'Partners', render: (o: Owner) => o._count?.partners ?? 0 },
    { key: 'users', header: 'Users', render: (o: Owner) => o._count?.users ?? 0 },
    { key: 'createdAt', header: 'Created', render: (o: Owner) => formatDate(o.createdAt) },
    {
      key: 'actions',
      header: 'Actions',
      render: (o: Owner) => (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={(e) => openEditModal(o, e)}>
            <HiPencil className="w-4 h-4" />
          </Button>
          <Button variant="danger" size="sm" onClick={(e) => openDeleteModal(o, e)}>
            <HiTrash className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  const createFields = (
    <div className="space-y-4">
      {linkableUsers && linkableUsers.length === 0 ? (
        <div className="text-sm bg-amber-50 text-amber-700 rounded-lg px-3 py-2">
          Kein verknüpfbarer Benutzer vorhanden. Bitte zuerst unter <strong>Users</strong> einen Benutzer mit
          Rolle <strong>Owner</strong> anlegen (E-Mail + Rolle), dann hier auswählen.
        </div>
      ) : (
        <Select
          label="Benutzer (Rolle Owner)"
          options={[
            { value: '', label: 'Benutzer auswählen…' },
            ...(linkableUsers?.map((u) => ({
              value: u.id,
              label: `${u.email} (${u.firstName} ${u.lastName})`,
            })) || []),
          ]}
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            const sel = linkableUsers?.find((u) => u.id === e.target.value);
            setEmail(sel?.email || '');
            if (sel && !name) setName(`${sel.firstName} ${sel.lastName}`.trim());
          }}
          required
        />
      )}
      {email && <p className="-mt-2 text-xs text-gray-500">E-Mail: {email}</p>}
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
    </div>
  );

  const editFields = (
    <div className="space-y-4">
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <Input
        type="email"
        label="Email (vom verknüpften Benutzer)"
        value={email}
        disabled
        className="bg-gray-50 text-gray-500"
      />
      <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
    </div>
  );

  return (
    <div>
      <CardHeader
        title="Owners"
        description="Oberste Ebene der Hierarchie (Owner → Partner → Kunde)"
        action={
          <Button onClick={() => setIsCreateOpen(true)}>
            <HiPlus className="w-4 h-4 mr-2" />
            Neuer Owner
          </Button>
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Owner suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(owner) => owner.id}
          isLoading={isLoading}
          emptyMessage="Keine Owner gefunden"
          onRowClick={(owner) => navigate(`/owners/${owner.id}`)}
        />

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Neuer Owner">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          {createFields}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" isLoading={createMutation.isPending} disabled={!userId}>
              Owner erstellen
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedOwner(null);
          resetForm();
        }}
        title="Owner bearbeiten"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate();
          }}
        >
          {editFields}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditOpen(false);
                setSelectedOwner(null);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedOwner(null);
        }}
        title="Owner löschen"
      >
        <p className="text-gray-600 mb-6">
          <strong>{selectedOwner?.name}</strong> wirklich deaktivieren?
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteOpen(false);
              setSelectedOwner(null);
            }}
          >
            Abbrechen
          </Button>
          <Button variant="danger" onClick={() => deleteMutation.mutate()} isLoading={deleteMutation.isPending}>
            Löschen
          </Button>
        </div>
      </Modal>
    </div>
  );
}
